import { useEffect, useRef, useState, FunctionComponent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SafeAreaTop from 'src/designSystem/SafeArea/SafeAreaTop';
import SafeAreaBottom from 'src/designSystem/SafeArea/SafeAreaBottom';
import CloseButton from 'src/designSystem/CloseButton';
import { FEEDBACK_ORIGIN } from 'src/config/externalLinks';
import type { Country } from 'src/types/country';
import { useFocusTrap } from 'src/hooks/useFocusTrap';

// The page version follows the spoken language, so Belgian and Swiss parents
// get the version of their language; the country is passed on for reporting only.
const MARKET_BY_LANGUAGE: Record<string, string> = {
  fr: 'fr',
  pt: 'br',
  it: 'it',
  de: 'de',
};
const FALLBACK_MARKET = 'en';

type Props = {
  familyCode: string;
  language: string;
  country?: Country;
  onClose: () => void;
};

const FeedbackModal: FunctionComponent<Props> = ({
  familyCode,
  language,
  country,
  onClose,
}) => {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef);

  const market =
    MARKET_BY_LANGUAGE[language.split('-')[0].toLowerCase()] ??
    FALLBACK_MARKET;
  const countryParam = country ? `&country=${country}` : '';
  const src = `${FEEDBACK_ORIGIN}/?code=${encodeURIComponent(
    familyCode,
  )}&market=${market}${countryParam}&embed=1`;

  // Not closed here: the final screen still asks for a phone number.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== FEEDBACK_ORIGIN) {
        return;
      }
      if (event.data?.type === 'bao-feedback:done') {
        setDone(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('voiceFeedback.title')}
      tabIndex={-1}
      data-testid="voice-feedback-modal"
      className="fixed inset-y-0 fixed-in-column z-modal flex flex-col bg-white outline-none"
    >
      <SafeAreaTop />
      <header className="flex flex-none items-center justify-between border-b border-structural-lightest py-2.5 pl-4 pr-2">
        <span className="text-base font-bold text-structural-darkest">
          {done ? t('voiceFeedback.doneTitle') : t('voiceFeedback.title')}
        </span>
        <CloseButton
          onClick={onClose}
          className="m-0"
          data-testid="voice-feedback-close"
        />
      </header>

      <iframe
        title={t('voiceFeedback.iframeTitle')}
        src={src}
        allow="microphone"
        data-testid="voice-feedback-iframe"
        className="w-full flex-1 border-0"
      />
      <SafeAreaBottom />
    </div>,
    document.body,
  );
};

export default FeedbackModal;
